import { useState, useCallback } from 'react';
import { SYSTEM_PROMPT } from '../data/curriculum';

export function useChat(lessonContext = null) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const systemPrompt = lessonContext
    ? `${SYSTEM_PROMPT}\n\nCurrent lesson: "${lessonContext.title}" — ${lessonContext.description}\nChallenge for this lesson: ${lessonContext.challenge}\n\nGuide the user through this lesson. When they've completed the challenge, celebrate and mark it done.`
    : `${SYSTEM_PROMPT}\n\nThe user is in free coaching mode. Help them with any AI question they have.`;

  const sendMessage = useCallback(async (userText) => {
    if (!userText.trim() || loading) return;

    const userMsg = { role: 'user', content: userText };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);
    setError(null);

    try {
      const apiKey = process.env.REACT_APP_ANTHROPIC_API_KEY;
      if (!apiKey || apiKey === 'your_anthropic_api_key_here') {
        throw new Error('API key not configured. Add REACT_APP_ANTHROPIC_API_KEY to your .env.local file.');
      }

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1024,
          system: systemPrompt,
          messages: newMessages,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error?.message || 'API error');
      }

      const data = await response.json();
      const reply = data.content?.map(b => b.text || '').join('') || "Let me think on that...";
      setMessages([...newMessages, { role: 'assistant', content: reply }]);
    } catch (err) {
      setError(err.message);
      setMessages([...newMessages, {
        role: 'assistant',
        content: `Hmm, I hit a snag: ${err.message}. Try again in a moment! 🔄`
      }]);
    }
    setLoading(false);
  }, [messages, loading, systemPrompt]);

  const startLesson = useCallback((lesson) => {
    setMessages([{
      role: 'assistant',
      content: `Hey! Ready to unlock **${lesson.title}**? 🎯\n\n${lesson.description}\n\n**Your challenge:** ${lesson.challenge}\n\nLet's start — what's your biggest question or hesitation about this topic?`,
    }]);
  }, []);

  const clearChat = useCallback(() => setMessages([]), []);

  return { messages, loading, error, sendMessage, startLesson, clearChat };
}
